import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { categoryLabel } from "@/lib/serviceCategory";
import { platformApi } from "@/services/api/platform";
import type {
  PlatformService,
  ServiceTier,
  ServiceTierCreateRequest,
  ServiceTierUpdateRequest,
  ServiceFeature,
  ServiceFeatureUpdateRequest,
} from "@/services/api/platform";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Layers,
  DollarSign,
  Database,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import { useServerPagination } from "@/hooks/useServerPagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export const Route = createFileRoute("/portal/platform/pricing")({
  component: PricingPage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPrice(cents: number, currency: "zar" | "usd"): string {
  if (cents === 0) return "–";
  const value = cents / 100;
  const symbol = currency === "zar" ? "R" : "$";
  return `${symbol}${value.toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function PricingPage() {
  const { token } = useAuth();
  const [services, setServices] = useState<PlatformService[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    offset,
    searchTerm,
    setSearchTerm,
    getPaginationInfo,
  } = useServerPagination({ initialItemsPerPage: 25 });

  const [sortBy, setSortBy] = useState("sort_order");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const loadServices = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await platformApi.listPlatformServicesPaginated(token, {
        include_inactive: showInactive,
        search: searchTerm || undefined,
        category: categoryFilter !== "all" ? categoryFilter : undefined,
        offset,
        limit: itemsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setServices(result.items);
      setTotal(result.total);
    } catch {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, [token, showInactive, categoryFilter, searchTerm, offset, itemsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const { totalPages, startIndex, endIndex } = getPaginationInfo(total);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  function handleSort(key: string) {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  }

  function getSortDirection(key: string): "asc" | "desc" | null {
    return sortBy === key ? sortOrder : null;
  }

  const platformCount = services.filter((s) => s.category === "platform").length;
  const connectorCount = services.filter((s) => s.category === "connector").length;
  const managedServiceCount = services.filter(
    (s) => s.category === "managed_service",
  ).length;
  const activeCount = services.filter((s) => s.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Pricing</h1>
          <p className="mt-2 text-lg text-slate-600">
            Service catalog — tiers, features, and pricing
          </p>
        </div>
      </div>

      {/* Content panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search services..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select value={categoryFilter} onValueChange={(val) => { setCategoryFilter(val); setCurrentPage(1); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="platform">Platform</SelectItem>
              <SelectItem value="connector">Connector</SelectItem>
              <SelectItem value="managed_service">Managed services</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
              id="show-inactive"
            />
            <Label htmlFor="show-inactive" className="text-sm text-slate-600">
              Show inactive
            </Label>
          </div>
          <Button variant="outline" size="sm" onClick={loadServices}>
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Stats cards */}
        <div className="grid gap-4 md:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Services</CardTitle>
              <Layers className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Platform</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{platformCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Connector</CardTitle>
              <Layers className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{connectorCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Managed services</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="pricing-managed-service-count">
                {managedServiceCount}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
              <Layers className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-12">
            <Layers className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm
                ? "No services match your search"
                : "No services configured"}
            </h3>
            <p className="text-muted-foreground">
              {searchTerm
                ? "Try a different search term."
                : "Services will appear here once configured."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("display_name")}
                  onSort={() => handleSort("display_name")}
                >
                  Service
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("category")}
                  onSort={() => handleSort("category")}
                >
                  Category
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("is_active")}
                  onSort={() => handleSort("is_active")}
                >
                  Status
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("sort_order")}
                  onSort={() => handleSort("sort_order")}
                >
                  Order
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="w-[50px]"
                >
                  {" "}
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((service) => (
                <Fragment key={service.id}>
                  <TableRow
                    className={`cursor-pointer hover:bg-slate-50 ${!service.is_active ? "opacity-60" : ""}`}
                    onClick={() =>
                      setExpandedId((prev) =>
                        prev === service.id ? null : service.id,
                      )
                    }
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {expandedId === service.id ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        )}
                        <div>
                          <div className="font-medium">
                            {service.display_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {service.description || service.name}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          service.category === "connector"
                            ? "outline"
                            : "secondary"
                        }
                      >
                        {categoryLabel(service.category)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={service.is_active ? "default" : "secondary"}
                      >
                        {service.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-mono text-sm">
                      {service.sort_order}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedId((prev) =>
                            prev === service.id ? null : service.id,
                          );
                        }}
                      >
                        {expandedId === service.id ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expandedId === service.id && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={6} className="bg-slate-50/50 px-6 py-4">
                        <ServiceDetail
                          service={service}
                          showInactive={showInactive}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalCount={total}
              pageSize={itemsPerPage}
              pageSizeOptions={[10, 25, 50]}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onPageChange={setCurrentPage}
              onPageSizeChange={setItemsPerPage}
              onFirstPage={() => setCurrentPage(1)}
              onLastPage={() => setCurrentPage(totalPages)}
              onPrevPage={() => setCurrentPage(currentPage - 1)}
              onNextPage={() => setCurrentPage(currentPage + 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expanded service detail — tiers + features
// ---------------------------------------------------------------------------

function ServiceDetail({
  service,
  showInactive,
}: {
  service: PlatformService;
  showInactive: boolean;
}) {
  const { token } = useAuth();
  const [tiers, setTiers] = useState<ServiceTier[]>([]);
  const [features, setFeatures] = useState<ServiceFeature[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [tiersResult, featuresResult] = await Promise.all([
        platformApi.listTiersForService(token, service.id, showInactive),
        platformApi.listServiceFeatures(token, service.id, showInactive),
      ]);
      setTiers(tiersResult.sort((a, b) => a.sort_order - b.sort_order));
      setFeatures(
        featuresResult.features.sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
        ),
      );
    } catch {
      toast.error(`Failed to load data for ${service.display_name}`);
    } finally {
      setLoading(false);
    }
  }, [token, service.id, service.display_name, showInactive]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TiersSection
        service={service}
        tiers={tiers}
        setTiers={setTiers}
        features={features}
        showInactive={showInactive}
      />
      <FeaturesSection
        service={service}
        features={features}
        setFeatures={setFeatures}
        showInactive={showInactive}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiers section
// ---------------------------------------------------------------------------

function TiersSection({
  service,
  tiers,
  setTiers,
  features,
  showInactive,
}: {
  service: PlatformService;
  tiers: ServiceTier[];
  setTiers: (t: ServiceTier[]) => void;
  features: ServiceFeature[];
  showInactive: boolean;
}) {
  const { token } = useAuth();
  const [tiersOpen, setTiersOpen] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTier, setEditingTier] = useState<ServiceTier | null>(null);
  const [deletingTier, setDeletingTier] = useState<ServiceTier | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Tier-feature checklist state
  const [tierFeatureIds, setTierFeatureIds] = useState<Set<string>>(new Set());
  const [loadingTierFeatures, setLoadingTierFeatures] = useState(false);
  const [togglingFeature, setTogglingFeature] = useState<string | null>(null);
  const [manualSlug, setManualSlug] = useState(false);
  // Position: "start" or tier ID to insert after
  const [insertAfter, setInsertAfter] = useState<string>("end");
  const [createForm, setCreateForm] = useState<ServiceTierCreateRequest>({
    service_id: service.id,
    name: "",
    display_name: "",
    sort_order: 0,
    price_monthly_zar: 0,
    price_monthly_usd: 0,
    price_annual_zar: 0,
    price_annual_usd: 0,
  });

  async function reloadTiers() {
    if (!token) return;
    const result = await platformApi.listTiersForService(
      token,
      service.id,
      showInactive,
    );
    setTiers(result.sort((a, b) => a.sort_order - b.sort_order));
  }

  async function handleCreateTier() {
    if (!token || !createForm.name || !createForm.display_name) return;
    setIsSaving(true);
    try {
      // Calculate sort_order from position
      const sorted = [...tiers].sort((a, b) => a.sort_order - b.sort_order);
      let newSortOrder: number;
      if (insertAfter === "start") {
        newSortOrder = 0;
      } else if (insertAfter === "end") {
        newSortOrder = sorted.length > 0 ? sorted[sorted.length - 1].sort_order + 1 : 0;
      } else {
        const afterTier = sorted.find((t) => t.id === insertAfter);
        newSortOrder = afterTier ? afterTier.sort_order + 1 : sorted.length;
      }

      // Re-number tiers that would collide (shift up by 1)
      for (const t of sorted) {
        if (t.sort_order >= newSortOrder) {
          await platformApi.updateTier(token, t.id, {
            sort_order: t.sort_order + 1,
          });
        }
      }

      await platformApi.createTier(token, {
        ...createForm,
        service_id: service.id,
        sort_order: newSortOrder,
      });
      toast.success(`Created tier: ${createForm.display_name}`);
      setShowCreateDialog(false);
      setManualSlug(false);
      setInsertAfter("end");
      setCreateForm({
        service_id: service.id,
        name: "",
        display_name: "",
        sort_order: 0,
        price_monthly_zar: 0,
        price_monthly_usd: 0,
        price_annual_zar: 0,
        price_annual_usd: 0,
      });
      await reloadTiers();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create tier";
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleUpdateTier(
    tierId: string,
    updates: ServiceTierUpdateRequest,
  ) {
    if (!token) return;
    try {
      await platformApi.updateTier(token, tierId, updates);
      toast.success("Tier updated");
      await reloadTiers();
    } catch {
      toast.error("Failed to update tier");
    }
  }

  async function loadTierFeatures(tierId: string) {
    if (!token) return;
    setLoadingTierFeatures(true);
    try {
      const links = await platformApi.listTierFeatures(token, tierId);
      setTierFeatureIds(new Set(links.map((l) => l.service_feature_id)));
    } catch {
      toast.error("Failed to load tier features");
    } finally {
      setLoadingTierFeatures(false);
    }
  }

  async function handleToggleTierFeature(
    tierId: string,
    featureId: string,
    checked: boolean,
  ) {
    if (!token) return;
    setTogglingFeature(featureId);
    try {
      if (checked) {
        await platformApi.addTierFeature(token, tierId, featureId);
      } else {
        await platformApi.removeTierFeature(token, tierId, featureId);
      }
      // Cascade changes other tiers — refresh the tier list + this tier's features
      await Promise.all([reloadTiers(), loadTierFeatures(tierId)]);
    } catch {
      toast.error(`Failed to ${checked ? "add" : "remove"} feature`);
    } finally {
      setTogglingFeature(null);
    }
  }

  function openEditTier(tier: ServiceTier) {
    setEditingTier({ ...tier });
    loadTierFeatures(tier.id);
  }

  async function handleSaveEditTier() {
    if (!token || !editingTier) return;
    setIsSaving(true);
    try {
      await platformApi.updateTier(token, editingTier.id, {
        display_name: editingTier.display_name,
        sort_order: editingTier.sort_order,
        price_monthly_zar: editingTier.price_monthly_zar,
        price_monthly_usd: editingTier.price_monthly_usd,
        price_annual_zar: editingTier.price_annual_zar,
        price_annual_usd: editingTier.price_annual_usd,
        allowances: editingTier.allowances ?? undefined,
        overage_pricing: editingTier.overage_pricing ?? undefined,
        contact_sales: editingTier.contact_sales,
        is_active: editingTier.is_active,
        sku_code: editingTier.sku_code ?? undefined,
      });
      toast.success(`Updated tier: ${editingTier.display_name}`);
      setEditingTier(null);
      await reloadTiers();
    } catch {
      toast.error("Failed to update tier");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteTier() {
    if (!token || !deletingTier) return;
    setDeleteSubmitting(true);
    try {
      await platformApi.deleteTier(token, deletingTier.id);
      toast.success(`Deleted tier: ${deletingTier.display_name}`);
      setDeletingTier(null);
      await reloadTiers();
    } catch {
      toast.error("Failed to delete tier");
    } finally {
      setDeleteSubmitting(false);
    }
  }

  // Build placeholder examples from sibling tiers for JSON fields
  function getAllowancePlaceholder(): string {
    const tierWithAllowances = tiers.find((t) => t.allowances && Object.keys(t.allowances).length > 0);
    if (!tierWithAllowances?.allowances) return '{\n  "key": 0\n}';
    // Show keys with zeroed values as template
    const template = Object.fromEntries(
      Object.keys(tierWithAllowances.allowances).map((k) => [k, 0]),
    );
    return JSON.stringify(template, null, 2);
  }

  function getOveragePlaceholder(): string {
    const tierWithOverage = tiers.find((t) => t.overage_pricing && Object.keys(t.overage_pricing).length > 0);
    if (!tierWithOverage?.overage_pricing) return '{\n  "per_unit_usd": 0\n}';
    const template = Object.fromEntries(
      Object.keys(tierWithOverage.overage_pricing).map((k) => [k, 0]),
    );
    return JSON.stringify(template, null, 2);
  }

  return (
    <>
      <Collapsible open={tiersOpen} onOpenChange={setTiersOpen}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground">
            {tiersOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <h3 className="text-sm font-semibold">Tiers & Pricing</h3>
            <Badge variant="outline" className="text-xs">
              {tiers.length}
            </Badge>
          </CollapsibleTrigger>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Tier
          </Button>
        </div>
        <CollapsibleContent className="mt-3">
          {tiers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No tiers configured.
            </p>
          ) : (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      Tier
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">
                      ZAR/month
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">
                      USD/month
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">
                      ZAR/year
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">
                      USD/year
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-center">
                      Features
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-center">
                      Contact Sales
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-center">
                      Active
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">
                      {" "}
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tiers.map((tier) => (
                    <TableRow key={tier.id}>
                      <TableCell>
                        <div>
                          <span className="font-medium">
                            {tier.display_name}
                          </span>
                          <span className="text-xs text-muted-foreground ml-2">
                            ({tier.name})
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatPrice(tier.price_monthly_zar, "zar")}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatPrice(tier.price_monthly_usd, "usd")}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatPrice(tier.price_annual_zar, "zar")}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatPrice(tier.price_annual_usd, "usd")}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {tier.features
                          ? Object.keys(tier.features).length
                          : 0}
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={tier.contact_sales}
                          onCheckedChange={(v) =>
                            handleUpdateTier(tier.id, { contact_sales: v })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={tier.is_active}
                          onCheckedChange={(v) =>
                            handleUpdateTier(tier.id, { is_active: v })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditTier(tier)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeletingTier(tier)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Create Tier Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tier to {service.display_name}</DialogTitle>
            <DialogDescription>
              Create a new pricing tier for this service.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  value={createForm.display_name}
                  onChange={(e) => {
                    const displayName = e.target.value;
                    setCreateForm((f) => ({
                      ...f,
                      display_name: displayName,
                      ...(!manualSlug && {
                        name: displayName
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "_")
                          .replace(/^_|_$/g, ""),
                      }),
                    }));
                  }}
                  placeholder="e.g. Starter Plus"
                />
              </div>
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => {
                    setManualSlug(true);
                    setCreateForm((f) => ({ ...f, name: e.target.value }));
                  }}
                  placeholder="e.g. starter_plus"
                />
                <p className="text-xs text-muted-foreground">
                  Auto-derived from display name
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Position</Label>
              <Select value={insertAfter} onValueChange={setInsertAfter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="start">At the beginning</SelectItem>
                  {[...tiers]
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        After {t.display_name}
                      </SelectItem>
                    ))}
                  <SelectItem value="end">At the end</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ZAR/month (cents)</Label>
                <Input
                  type="number"
                  value={createForm.price_monthly_zar}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      price_monthly_zar: Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>USD/month (cents)</Label>
                <Input
                  type="number"
                  value={createForm.price_monthly_usd}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      price_monthly_usd: Number(e.target.value),
                    }))
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ZAR/year (cents)</Label>
                <Input
                  type="number"
                  value={createForm.price_annual_zar}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      price_annual_zar: Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>USD/year (cents)</Label>
                <Input
                  type="number"
                  value={createForm.price_annual_usd}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      price_annual_usd: Number(e.target.value),
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>SKU Code</Label>
              <Input
                value={createForm.sku_code || ""}
                onChange={(e) =>
                  setCreateForm((f) => ({
                    ...f,
                    sku_code: e.target.value || undefined,
                  }))
                }
                placeholder="e.g. RAG-PRD-STR-001"
              />
              <p className="text-xs text-muted-foreground">
                Per-tier SKU / Xero Item code
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTier}
              disabled={
                isSaving || !createForm.name || !createForm.display_name
              }
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Tier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tier Dialog */}
      <Dialog
        open={!!editingTier}
        onOpenChange={(open) => !open && setEditingTier(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Tier: {editingTier?.display_name}</DialogTitle>
            <DialogDescription>
              Update pricing, allowances, and overage rates.
            </DialogDescription>
          </DialogHeader>
          {editingTier && (
            <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <Input
                    value={editingTier.display_name}
                    onChange={(e) =>
                      setEditingTier({
                        ...editingTier,
                        display_name: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={editingTier.sort_order}
                    onChange={(e) =>
                      setEditingTier({
                        ...editingTier,
                        sort_order: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>ZAR/month (cents)</Label>
                  <Input
                    type="number"
                    value={editingTier.price_monthly_zar}
                    onChange={(e) =>
                      setEditingTier({
                        ...editingTier,
                        price_monthly_zar: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>USD/month (cents)</Label>
                  <Input
                    type="number"
                    value={editingTier.price_monthly_usd}
                    onChange={(e) =>
                      setEditingTier({
                        ...editingTier,
                        price_monthly_usd: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>ZAR/year (cents)</Label>
                  <Input
                    type="number"
                    value={editingTier.price_annual_zar}
                    onChange={(e) =>
                      setEditingTier({
                        ...editingTier,
                        price_annual_zar: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>USD/year (cents)</Label>
                  <Input
                    type="number"
                    value={editingTier.price_annual_usd}
                    onChange={(e) =>
                      setEditingTier({
                        ...editingTier,
                        price_annual_usd: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>SKU Code</Label>
                <Input
                  value={editingTier.sku_code || ""}
                  onChange={(e) =>
                    setEditingTier({
                      ...editingTier,
                      sku_code: e.target.value || null,
                    })
                  }
                  placeholder="e.g. RAG-PRD-STR-001"
                />
                <p className="text-xs text-muted-foreground">
                  Per-tier SKU / Xero Item code
                </p>
              </div>
              <div className="space-y-2">
                <Label>Allowances (JSON)</Label>
                <textarea
                  className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  value={
                    editingTier.allowances && Object.keys(editingTier.allowances).length > 0
                      ? JSON.stringify(editingTier.allowances, null, 2)
                      : ""
                  }
                  placeholder={getAllowancePlaceholder()}
                  onChange={(e) => {
                    const val = e.target.value.trim();
                    if (!val) {
                      setEditingTier({ ...editingTier, allowances: null });
                      return;
                    }
                    try {
                      setEditingTier({
                        ...editingTier,
                        allowances: JSON.parse(e.target.value),
                      });
                    } catch {
                      /* ignore invalid JSON while typing */
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Usage quotas — use -1 for unlimited
                </p>
              </div>
              <div className="space-y-2">
                <Label>Overage Pricing (JSON)</Label>
                <textarea
                  className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  value={
                    editingTier.overage_pricing && Object.keys(editingTier.overage_pricing).length > 0
                      ? JSON.stringify(editingTier.overage_pricing, null, 2)
                      : ""
                  }
                  placeholder={getOveragePlaceholder()}
                  onChange={(e) => {
                    const val = e.target.value.trim();
                    if (!val) {
                      setEditingTier({ ...editingTier, overage_pricing: null });
                      return;
                    }
                    try {
                      setEditingTier({
                        ...editingTier,
                        overage_pricing: JSON.parse(e.target.value),
                      });
                    } catch {
                      /* ignore invalid JSON while typing */
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Per-unit overage rates in cents
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingTier.contact_sales}
                    onCheckedChange={(v) =>
                      setEditingTier({ ...editingTier, contact_sales: v })
                    }
                  />
                  <Label>Contact Sales</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingTier.is_active}
                    onCheckedChange={(v) =>
                      setEditingTier({ ...editingTier, is_active: v })
                    }
                  />
                  <Label>Active</Label>
                </div>
              </div>

              {/* Feature checklist — toggle which features are included in this tier */}
              <div className="space-y-2">
                <Label>Included Features</Label>
                {loadingTierFeatures ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading features...
                  </div>
                ) : features.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-1">
                    No features defined for this service yet.
                  </p>
                ) : (
                  <div className="rounded-md border p-3 space-y-2 max-h-[200px] overflow-y-auto">
                    {features
                      .filter((f) => f.is_active)
                      .map((feat) => (
                        <div
                          key={feat.id}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={tierFeatureIds.has(feat.id)}
                              disabled={togglingFeature === feat.id}
                              onCheckedChange={(checked) =>
                                handleToggleTierFeature(
                                  editingTier.id,
                                  feat.id,
                                  checked,
                                )
                              }
                            />
                            <span className="text-sm">
                              {feat.display_name}
                            </span>
                            {feat.sku_code && (
                              <code className="text-xs text-muted-foreground">
                                {feat.sku_code}
                              </code>
                            )}
                          </div>
                          {togglingFeature === feat.id && (
                            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingTier(null)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEditTier} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Tier Confirmation */}
      <AlertDialog
        open={!!deletingTier}
        onOpenChange={(open) => !open && setDeletingTier(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tier?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deletingTier?.display_name}&rdquo;?
              This will deactivate the tier. Existing subscriptions on this tier
              will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTier}
              disabled={deleteSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete Tier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Features section
// ---------------------------------------------------------------------------

function FeaturesSection({
  service,
  features,
  setFeatures,
  showInactive,
}: {
  service: PlatformService;
  features: ServiceFeature[];
  setFeatures: (f: ServiceFeature[]) => void;
  showInactive: boolean;
}) {
  const { token } = useAuth();
  const [featuresOpen, setFeaturesOpen] = useState(true);
  const [editingFeature, setEditingFeature] = useState<ServiceFeature | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);

  async function reloadFeatures() {
    if (!token) return;
    const result = await platformApi.listServiceFeatures(
      token,
      service.id,
      showInactive,
    );
    setFeatures(
      result.features.sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
      ),
    );
  }

  async function handleSaveEditFeature() {
    if (!token || !editingFeature) return;
    setIsSaving(true);
    try {
      const update: ServiceFeatureUpdateRequest = {
        display_name: editingFeature.display_name,
        description: editingFeature.description ?? undefined,
        sku_code: editingFeature.sku_code ?? undefined,
        price_monthly_zar: editingFeature.price_monthly_zar,
        price_monthly_usd: editingFeature.price_monthly_usd,
        is_base: editingFeature.is_base,
        is_active: editingFeature.is_active,
        sort_order: editingFeature.sort_order,
      };
      await platformApi.updateServiceFeature(
        token,
        editingFeature.id,
        update,
      );
      toast.success(`Updated feature: ${editingFeature.display_name}`);
      setEditingFeature(null);
      await reloadFeatures();
    } catch {
      toast.error("Failed to update feature");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <Collapsible open={featuresOpen} onOpenChange={setFeaturesOpen}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger className="flex items-center gap-2 hover:text-foreground">
            {featuresOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <h3 className="text-sm font-semibold">Features</h3>
            <Badge variant="outline" className="text-xs">
              {features.length}
            </Badge>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="mt-3">
          {features.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No features configured.
            </p>
          ) : (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      Feature
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      SKU
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">
                      ZAR/month
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">
                      USD/month
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-center">
                      Type
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="text-center">
                      Active
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">
                      {" "}
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {features.map((feat) => (
                    <TableRow key={feat.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium text-sm">
                            {feat.display_name}
                          </div>
                          <code className="text-xs text-muted-foreground">
                            {feat.feature_key}
                          </code>
                          {feat.description && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {feat.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {feat.sku_code || (
                          <span className="text-muted-foreground">–</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatPrice(feat.price_monthly_zar, "zar")}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatPrice(feat.price_monthly_usd, "usd")}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={feat.is_base ? "default" : "secondary"}
                        >
                          {feat.is_base ? "Base" : "Add-on"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={feat.is_active ? "default" : "secondary"}
                        >
                          {feat.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingFeature({ ...feat })}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Edit Feature Dialog */}
      <Dialog
        open={!!editingFeature}
        onOpenChange={(open) => !open && setEditingFeature(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Edit Feature: {editingFeature?.display_name}
            </DialogTitle>
          </DialogHeader>
          {editingFeature && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <Input
                    value={editingFeature.display_name}
                    onChange={(e) =>
                      setEditingFeature({
                        ...editingFeature,
                        display_name: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>SKU Code</Label>
                  <Input
                    value={editingFeature.sku_code || ""}
                    onChange={(e) =>
                      setEditingFeature({
                        ...editingFeature,
                        sku_code: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editingFeature.description || ""}
                  onChange={(e) =>
                    setEditingFeature({
                      ...editingFeature,
                      description: e.target.value,
                    })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>ZAR/month (cents)</Label>
                  <Input
                    type="number"
                    value={editingFeature.price_monthly_zar}
                    onChange={(e) =>
                      setEditingFeature({
                        ...editingFeature,
                        price_monthly_zar: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>USD/month (cents)</Label>
                  <Input
                    type="number"
                    value={editingFeature.price_monthly_usd}
                    onChange={(e) =>
                      setEditingFeature({
                        ...editingFeature,
                        price_monthly_usd: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingFeature.is_base}
                    onCheckedChange={(v) =>
                      setEditingFeature({ ...editingFeature, is_base: v })
                    }
                  />
                  <Label>Base Feature</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingFeature.is_active}
                    onCheckedChange={(v) =>
                      setEditingFeature({ ...editingFeature, is_active: v })
                    }
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingFeature(null)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEditFeature} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
